import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    ArrowRight,
    CheckCircle2,
    Copy,
    DollarSign,
    FileText,
    Loader2,
    Link2,
    MousePointerClick,
    ScrollText,
    TrendingUp,
    Users,
} from "lucide-react";
import { PartnerAPI } from "@/lib/partnerApi";
import { formatApiError } from "@/context/PartnerAuthContext";

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
        });
    } catch {
        return iso;
    }
}

export default function AffiliateHub() {
    const [me, setMe] = useState(null);
    const [stats, setStats] = useState(null);
    const [agreementOpen, setAgreementOpen] = useState(false);
    const [agreementChecked, setAgreementChecked] = useState(false);
    const [signing, setSigning] = useState(false);
    const [copied, setCopied] = useState(false);

    const load = async () => {
        try {
            const [m, s] = await Promise.all([
                PartnerAPI.affiliateMe(),
                PartnerAPI.affiliateStats(),
            ]);
            setMe(m);
            setStats(s);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        }
    };
    useEffect(() => {
        load();
    }, []);

    if (!me || !stats) {
        return (
            <div className="py-16 text-center">
                <Loader2 size={28} className="animate-spin inline text-gold" />
            </div>
        );
    }

    // Public share link — resolved to current frontend origin
    const shareLink =
        typeof window !== "undefined"
            ? `${window.location.origin}/go/${me.slug}`
            : me.share_link;

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareLink);
            setCopied(true);
            toast.success("Link copied");
            setTimeout(() => setCopied(false), 2500);
        } catch {
            toast.error("Could not copy — please copy manually");
        }
    };

    const sign = async () => {
        if (!agreementChecked) return;
        setSigning(true);
        try {
            await PartnerAPI.affiliateSign();
            toast.success("Affiliate agreement signed");
            setAgreementOpen(false);
            setAgreementChecked(false);
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSigning(false);
        }
    };

    return (
        <section
            className="space-y-10"
            data-testid="partner-affiliate-hub"
        >
            <header>
                <span className="divider-rule text-gold">
                    <TrendingUp size={12} /> EIGOO Affiliate Hub ·{" "}
                    Tier {me.tier || 1} · {me.tier_name || "Entry"}
                </span>
                <h2 className="font-serif text-4xl text-cream mt-5">
                    Earn{" "}
                    <span
                        className="italic text-gold"
                        data-testid="affiliate-rate-headline"
                    >
                        {me.rate_percent}% recurring
                    </span>{" "}
                    on every creator you refer
                </h2>
                <p className="mt-3 text-cream/60 max-w-2xl text-sm leading-relaxed">
                    Share your link with other creators. You can earn either way:
                    <br />
                    <strong className="text-cream">Refer and close</strong> — 20% of the new
                    customer's setup fee <em>plus</em> 10% of their monthly
                    subscription for as long as they stay.
                    <br />
                    <strong className="text-cream">Refer only</strong> — 10% of their setup
                    fee, one time.
                    <br />
                    Paid out monthly.
                </p>
            </header>

            {/* Agreement status */}
            {!me.agreement_signed ? (
                <div
                    className="p-6 bg-gold/10 border border-gold rounded-sm backdrop-blur"
                    data-testid="affiliate-agreement-required"
                >
                    <div className="flex items-start gap-4 flex-wrap">
                        <FileText size={22} className="text-gold shrink-0 mt-1" />
                        <div className="flex-1 min-w-0">
                            <p className="font-serif text-xl text-cream">
                                One step before you can share your link
                            </p>
                            <p className="text-cream/70 text-sm mt-1">
                                Review and sign the Affiliate Agreement to
                                activate commission tracking.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setAgreementOpen(true)}
                            data-testid="affiliate-open-agreement-btn"
                            className="btn-pill btn-gold text-xs shrink-0"
                        >
                            Review Agreement <ArrowRight size={12} />
                        </button>
                    </div>
                </div>
            ) : (
                <div
                    className="p-5 bg-cream/5 border border-gold/40 rounded-sm backdrop-blur flex gap-3 items-center flex-wrap"
                    data-testid="affiliate-agreement-signed"
                >
                    <CheckCircle2 size={22} className="text-gold shrink-0" />
                    <div className="flex-1">
                        <p className="font-serif text-base text-cream">
                            Affiliate Agreement on file
                        </p>
                        <p className="text-cream/55 text-xs mt-0.5">
                            Signed{" "}
                            <span data-testid="affiliate-signed-at">
                                {fmtDate(me.agreement_signed_at)}
                            </span>{" "}
                            · {me.agreement_version}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setAgreementOpen(true)}
                        className="btn-pill btn-ghost-light text-xs"
                        data-testid="affiliate-view-agreement-btn"
                    >
                        <ScrollText size={12} /> View
                    </button>
                </div>
            )}

            {/* Share link */}
            <div
                className="p-6 bg-cream text-forest rounded-sm"
                data-testid="affiliate-share-link-card"
            >
                <p className="text-[0.65rem] tracking-[0.3em] uppercase text-brown inline-flex items-center gap-1.5">
                    <Link2 size={12} /> Your Referral Link
                </p>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <code
                        data-testid="affiliate-share-link"
                        className="flex-1 min-w-0 overflow-x-auto whitespace-nowrap font-mono text-sm bg-cream-warm border border-forest/15 px-4 py-3 rounded-sm text-forest"
                    >
                        {shareLink}
                    </code>
                    <button
                        type="button"
                        onClick={copyLink}
                        data-testid="affiliate-copy-link-btn"
                        className="btn-pill btn-primary text-xs shrink-0"
                    >
                        {copied ? (
                            <>
                                <CheckCircle2 size={13} /> Copied
                            </>
                        ) : (
                            <>
                                <Copy size={13} /> Copy
                            </>
                        )}
                    </button>
                </div>
                <p className="mt-3 text-xs text-brown">
                    Share this link anywhere — social, DMs, Linktree. Clicks
                    land on <strong>{me.destination}</strong> and attribute
                    back to you via <code>ref={me.slug}</code>.
                </p>
            </div>

            {/* Stats */}
            <div
                className="grid grid-cols-2 md:grid-cols-4 gap-4"
                data-testid="affiliate-stats"
            >
                <StatCard
                    icon={MousePointerClick}
                    label="Clicks"
                    value={stats.clicks}
                    testid="affiliate-stat-clicks"
                />
                <StatCard
                    icon={Users}
                    label="Conversions"
                    value={`${stats.conversions_active}/${stats.conversions_total}`}
                    sub="active / total"
                    testid="affiliate-stat-conversions"
                />
                <StatCard
                    icon={DollarSign}
                    label="Monthly Commission"
                    value={`$${Number(stats.monthly_recurring_commission).toFixed(2)}`}
                    testid="affiliate-stat-monthly"
                    gold
                />
                <StatCard
                    icon={TrendingUp}
                    label="Lifetime Earnings"
                    value={`$${Number(stats.lifetime_commission).toFixed(2)}`}
                    testid="affiliate-stat-lifetime"
                />
            </div>

            {stats.conversions_total === 0 && (
                <p className="text-cream/50 text-xs text-center">
                    No conversions yet — share your link to start earning.
                </p>
            )}

            {/* Agreement modal */}
            {agreementOpen && (
                <AgreementModal
                    me={me}
                    checked={agreementChecked}
                    setChecked={setAgreementChecked}
                    signing={signing}
                    onSign={sign}
                    onClose={() => {
                        setAgreementOpen(false);
                        setAgreementChecked(false);
                    }}
                />
            )}
        </section>
    );
}

function StatCard({ icon: Icon, label, value, sub, gold, testid }) {
    return (
        <div
            data-testid={testid}
            className={`p-5 rounded-sm border backdrop-blur ${
                gold
                    ? "bg-forest border-gold/60"
                    : "bg-cream/5 border-cream/15"
            }`}
        >
            <Icon size={16} className={gold ? "text-gold" : "text-cream/70"} />
            <p
                className={`font-serif text-3xl md:text-4xl mt-3 ${
                    gold ? "text-gold" : "text-cream"
                }`}
            >
                {value}
            </p>
            <p className="mt-1 text-[0.6rem] tracking-[0.28em] uppercase text-cream/55">
                {label}
            </p>
            {sub && (
                <p className="text-[0.6rem] text-cream/40 mt-0.5">{sub}</p>
            )}
        </div>
    );
}

function AgreementModal({ me, checked, setChecked, signing, onSign, onClose }) {
    const signed = me.agreement_signed;
    return (
        <div
            className="fixed inset-0 z-50 bg-forest-deep/85 backdrop-blur flex items-center justify-center p-4"
            data-testid="affiliate-agreement-modal"
            role="dialog"
        >
            <div className="bg-cream text-forest rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <header className="p-6 md:p-8 border-b border-forest/10 flex items-center gap-3">
                    <ScrollText size={20} className="text-gold" />
                    <h3 className="font-serif text-2xl">
                        EIGOO Affiliate Agreement
                    </h3>
                    <span className="ml-auto text-[0.6rem] tracking-[0.28em] uppercase text-brown">
                        {me.agreement_version}
                    </span>
                </header>
                <div className="p-6 md:p-8 space-y-4 text-brown leading-relaxed text-[0.95rem]">
                    <p>
                        This Affiliate Agreement is entered into between{" "}
                        <strong className="text-forest">EIGOO Inc.</strong>{" "}
                        (the Program Operator) and the undersigned Partner{" "}
                        <span className="font-mono text-sm text-forest">
                            ({me.email})
                        </span>{" "}
                        (the Affiliate), separately from and in addition to any
                        other NDA or Creator Agreement in effect.
                    </p>

                    <h4 className="font-serif text-xl text-forest pt-2">
                        1. Program & Destination
                    </h4>
                    <p>
                        The Affiliate is granted a unique referral slug —{" "}
                        <code className="bg-cream-warm px-1.5 py-0.5 rounded">
                            {me.slug}
                        </code>{" "}
                        — and share link that routes referred visitors to the
                        EIGOO Creator Hub at{" "}
                        <strong className="text-forest">
                            {me.destination}
                        </strong>
                        . Clicks are tracked via a 48-hour cookie plus
                        last-click attribution at the point of signup.
                    </p>

                    <h4 className="font-serif text-xl text-forest pt-2">
                        2. Commission &amp; Rate
                    </h4>
                    <p>
                        For every creator who signs up through the Affiliate's
                        link and activates a paid Creator Hub subscription, the
                        Affiliate earns a{" "}
                        <strong className="text-forest">
                            {me.rate_percent}% recurring commission
                        </strong>{" "}
                        of that creator's monthly subscription fee, paid for
                        the full lifetime that the referred creator remains an
                        active paying subscriber.
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-sm">
                        <li>
                            Rate model:{" "}
                            <strong className="text-forest">
                                {me.rate_model.replace(/_/g, " ")}
                            </strong>
                        </li>
                        <li>
                            Current tier:{" "}
                            <strong className="text-forest">
                                Tier {me.tier || 1} · {me.tier_name || "Entry"}
                            </strong>
                            {" "}— refer-and-close pays 20% of setup fee + 10%
                            of monthly subscription for as long as the customer
                            stays. Refer-only pays 10% of setup, one time.
                        </li>
                        <li>Payout cadence: monthly, net-30 via ACH.</li>
                        <li>
                            Minimum payout threshold: $25 (balance rolls forward
                            below threshold).
                        </li>
                        <li>
                            Commission is calculated on NET subscription
                            revenue (post-Stripe fees).
                        </li>
                    </ul>

                    <div
                        className="mt-4 bg-cream-warm border border-forest/10 rounded-sm p-5"
                        data-testid="affiliate-tier-ladder"
                    >
                        <p className="text-[0.65rem] tracking-[0.3em] uppercase text-gold">
                            Commission Ladder
                        </p>
                        <div className="mt-3 grid sm:grid-cols-3 gap-4">
                            {(me.commission_tiers || []).map((t) => (
                                <div
                                    key={t.tier}
                                    data-testid={`affiliate-tier-${t.tier}`}
                                    className={`p-4 rounded-sm border ${
                                        t.default
                                            ? "bg-gold/20 border-gold"
                                            : "border-forest/10"
                                    }`}
                                >
                                    <p className="text-[0.6rem] tracking-[0.28em] uppercase text-brown">
                                        Tier {t.tier} · {t.name}
                                    </p>
                                    <p className="font-serif text-2xl text-forest mt-1">
                                        {typeof t.rate_percent === "number"
                                            ? `${t.rate_percent}%`
                                            : "—"}
                                    </p>
                                    <p className="text-[0.65rem] text-brown mt-1 leading-snug">
                                        {t.requirements}
                                    </p>
                                </div>
                            ))}
                        </div>
                        <p className="mt-3 text-xs text-brown italic">
                            Advancement to Rising / Flagship is governed by
                            EIGOO infrastructure policy — performance,
                            referral volume, and tenure. Rate set by the
                            Director at promotion.
                        </p>
                    </div>

                    <h4 className="font-serif text-xl text-forest pt-2">
                        3. Conduct &amp; Compliance
                    </h4>
                    <p>
                        The Affiliate agrees to represent EIGOO truthfully, to
                        disclose the affiliate relationship when required by
                        applicable law (FTC endorsement guidelines), and to not
                        engage in spam, trademark infringement, incentivized
                        self-referrals, or cookie-stuffing. Violations
                        terminate the agreement and forfeit unpaid commissions.
                    </p>

                    <h4 className="font-serif text-xl text-forest pt-2">
                        4. Term &amp; Termination
                    </h4>
                    <p>
                        This agreement is effective upon signature and remains
                        in force until terminated by either party in writing
                        with 30 days' notice. Earned recurring commissions on
                        pre-termination referrals continue to accrue so long
                        as those creators remain active, subject to Section 3.
                    </p>

                    <h4 className="font-serif text-xl text-forest pt-2">
                        5. Governing Law
                    </h4>
                    <p>
                        This agreement is governed by the laws of the State of
                        Hawai&apos;i. Disputes shall be resolved in Honolulu
                        County.
                    </p>

                    <div
                        className="mt-6 pt-6 border-t border-forest/10 grid sm:grid-cols-2 gap-6"
                        data-testid="affiliate-agreement-signatures"
                    >
                        <div>
                            <p className="text-[0.6rem] tracking-[0.28em] uppercase text-brown">
                                Counter-signatory · EIGOO Inc.
                            </p>
                            <p
                                className="font-serif text-xl text-forest mt-2"
                                data-testid="affiliate-eigoo-signatory-name"
                            >
                                Dontae Kirk
                            </p>
                            <p
                                className="text-xs text-brown mt-1"
                                data-testid="affiliate-eigoo-signatory-title"
                            >
                                Director, EIGOO Inc.
                            </p>
                        </div>
                        <div>
                            <p className="text-[0.6rem] tracking-[0.28em] uppercase text-brown">
                                Affiliate
                            </p>
                            <p className="font-serif text-xl text-forest mt-2">
                                {me.email}
                            </p>
                            <p className="text-xs text-brown mt-1">
                                {signed
                                    ? `Signed ${fmtDate(me.agreement_signed_at)}`
                                    : "Signature captured on acceptance below"}
                            </p>
                        </div>
                    </div>
                </div>

                <footer
                    className="p-6 md:p-8 bg-cream-warm border-t border-forest/10"
                    data-testid="affiliate-agreement-footer"
                >
                    {signed ? (
                        <div className="flex items-center gap-3">
                            <CheckCircle2
                                size={22}
                                className="text-gold shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                                <p className="font-serif text-base text-forest">
                                    You signed this agreement on{" "}
                                    {fmtDate(me.agreement_signed_at)}
                                </p>
                                <p className="text-brown text-xs">
                                    Reference: {me.agreement_version}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                className="btn-pill btn-primary text-xs"
                                data-testid="affiliate-agreement-close-signed"
                            >
                                Close
                            </button>
                        </div>
                    ) : (
                        <>
                            <label className="flex items-start gap-3 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => setChecked(e.target.checked)}
                                    data-testid="affiliate-agreement-checkbox"
                                    className="mt-1 w-5 h-5 accent-[var(--ig-gold)] shrink-0"
                                />
                                <span className="text-brown leading-relaxed">
                                    I have read and agree to the EIGOO
                                    Affiliate Agreement above, including the{" "}
                                    {me.rate_percent}% recurring commission
                                    rate, net-30 payout terms, and FTC
                                    disclosure requirements.
                                </span>
                            </label>
                            <div className="mt-6 flex gap-3 flex-wrap">
                                <button
                                    type="button"
                                    onClick={onSign}
                                    disabled={!checked || signing}
                                    data-testid="affiliate-agreement-sign-btn"
                                    className="btn-pill btn-gold disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {signing ? (
                                        <>
                                            <Loader2
                                                size={13}
                                                className="animate-spin"
                                            />{" "}
                                            Signing…
                                        </>
                                    ) : (
                                        <>
                                            Sign &amp; Activate{" "}
                                            <ArrowRight size={13} />
                                        </>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    data-testid="affiliate-agreement-cancel"
                                    className="btn-pill btn-ghost text-xs"
                                >
                                    Cancel
                                </button>
                            </div>
                        </>
                    )}
                </footer>
            </div>
        </div>
    );
}
